/**
 * @fileoverview Offline queue models for mutation persistence
 * @description Defines queue structure for failed operations that need retry
 * @project AIEMD Platform - Resilience System
 * @created 2025-01-18
 */

/**
 * Operation type for queue classification
 */
export enum QueuedOperationType {
  /** Creating new record */
  CREATE = 'create',

  /** Updating existing record */
  UPDATE = 'update',

  /** Deleting record */
  DELETE = 'delete',

  /** Uploading file */
  UPLOAD = 'upload',

  /** Calling RPC function */
  RPC = 'rpc',

  /** Custom mutation */
  CUSTOM = 'custom',
}

/**
 * Priority level for queue processing
 */
export enum QueuePriority {
  /** Low priority, process after others */
  LOW = 0,

  /** Normal priority */
  NORMAL = 1,

  /** High priority, process first */
  HIGH = 2,

  /** Critical, process immediately */
  CRITICAL = 3,
}

/**
 * Status of queued operation
 */
export enum QueuedOperationStatus {
  /** Waiting to be processed */
  PENDING = 'pending',

  /** Currently being processed */
  PROCESSING = 'processing',

  /** Successfully completed */
  COMPLETED = 'completed',

  /** Failed after retries */
  FAILED = 'failed',

  /** Cancelled by user */
  CANCELLED = 'cancelled',
}

/**
 * A queued operation waiting for retry
 */
export interface QueuedOperation {
  /** Unique ID for this operation */
  id: string;

  /** Type of operation */
  type: QueuedOperationType;

  /** Priority level */
  priority: QueuePriority;

  /** Current status */
  status: QueuedOperationStatus;

  /** Table or resource name */
  resource: string;

  /** Operation payload/data */
  payload: any;

  /** HTTP method if applicable */
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';

  /** local data query/RPC details */
  dbOperation?: {
    table?: string;
    rpcFunction?: string;
    query?: any;
  };

  /** When operation was queued */
  queuedAt: number;

  /** When operation was last attempted */
  lastAttemptAt?: number;

  /** Number of retry attempts */
  attemptCount: number;

  /** Maximum retry attempts allowed */
  maxAttempts: number;

  /** Last error encountered */
  lastError?: string;

  /** When operation expires from queue */
  expiresAt: number;

  /** User-friendly description */
  description?: string;

  /** Additional metadata */
  metadata?: Record<string, any>;
}

/**
 * Result of processing a queued operation
 */
export interface QueueProcessResult {
  /** Operation ID */
  operationId: string;

  /** Whether operation succeeded */
  success: boolean;

  /** Result data if successful */
  data?: any;

  /** Error if failed */
  error?: Error;

  /** Whether to retry later */
  shouldRetry: boolean;

  /** Number of attempts made */
  attempts: number;
}

/**
 * Queue statistics
 */
export interface QueueStats {
  /** Total operations in queue */
  total: number;

  /** Operations by status */
  byStatus: Record<QueuedOperationStatus, number>;

  /** Operations by type */
  byType: Record<QueuedOperationType, number>;

  /** Operations by priority */
  byPriority: Record<QueuePriority, number>;

  /** Oldest operation timestamp */
  oldestOperation?: number;

  /** Queue size in bytes (localStorage) */
  sizeBytes?: number;
}

/**
 * Configuration for offline queue
 */
export interface OfflineQueueConfig {
  /** Whether queue is enabled */
  enabled: boolean;

  /** Maximum number of operations to store */
  maxQueueSize: number;

  /** How long operations stay in queue (ms) */
  queueTTL: number;

  /** LocalStorage key for queue */
  storageKey: string;

  /** Maximum retry attempts per operation */
  maxRetries: number;

  /** Delay between retry attempts (ms) */
  retryDelay: number;

  /** Whether to auto-process queue on reconnect */
  autoProcessOnReconnect: boolean;

  /** Maximum concurrent processing */
  maxConcurrent: number;
}

/**
 * Default queue configuration
 */
export const DEFAULT_QUEUE_CONFIG: OfflineQueueConfig = {
  enabled: true,
  maxQueueSize: 100,
  queueTTL: 24 * 60 * 60 * 1000, // 24 hours
  storageKey: 'aiemd_offline_queue',
  maxRetries: 3,
  retryDelay: 2000,
  autoProcessOnReconnect: true,
  maxConcurrent: 3,
};

/**
 * Helper functions for queue management
 */

/**
 * Create a new queued operation
 */
export function createQueuedOperation(
  type: QueuedOperationType,
  resource: string,
  payload: any,
  options?: {
    priority?: QueuePriority;
    description?: string;
    maxAttempts?: number;
    metadata?: Record<string, any>;
  }
): QueuedOperation {
  const now = Date.now();
  const ttl = 24 * 60 * 60 * 1000; // 24 hours

  return {
    id: generateOperationId(),
    type,
    priority: options?.priority || QueuePriority.NORMAL,
    status: QueuedOperationStatus.PENDING,
    resource,
    payload,
    queuedAt: now,
    attemptCount: 0,
    maxAttempts: options?.maxAttempts || 3,
    expiresAt: now + ttl,
    description: options?.description,
    metadata: options?.metadata,
  };
}

/**
 * Generate unique operation ID
 */
function generateOperationId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Check if operation has expired
 */
export function isOperationExpired(operation: QueuedOperation): boolean {
  return Date.now() > operation.expiresAt;
}

/**
 * Check if operation can be retried
 */
export function canRetryOperation(operation: QueuedOperation): boolean {
  return (
    operation.status !== QueuedOperationStatus.COMPLETED &&
    operation.status !== QueuedOperationStatus.CANCELLED &&
    operation.attemptCount < operation.maxAttempts &&
    !isOperationExpired(operation)
  );
}

/**
 * Sort operations by priority and age
 */
export function sortOperationsByPriority(
  operations: QueuedOperation[]
): QueuedOperation[] {
  return [...operations].sort((a, b) => {
    // First by priority (higher first)
    if (a.priority !== b.priority) {
      return b.priority - a.priority;
    }
    // Then by age (older first)
    return a.queuedAt - b.queuedAt;
  });
}

/**
 * Filter operations by status
 */
export function filterByStatus(
  operations: QueuedOperation[],
  status: QueuedOperationStatus
): QueuedOperation[] {
  return operations.filter(op => op.status === status);
}

/**
 * Get pending operations ready for processing
 */
export function getPendingOperations(
  operations: QueuedOperation[]
): QueuedOperation[] {
  return operations
    .filter(
      op =>
        op.status === QueuedOperationStatus.PENDING &&
        !isOperationExpired(op) &&
        canRetryOperation(op)
    )
    .sort((a, b) => {
      // Priority first
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      // Then by queued time
      return a.queuedAt - b.queuedAt;
    });
}

/**
 * Calculate queue statistics
 */
export function calculateQueueStats(
  operations: QueuedOperation[]
): QueueStats {
  const stats: QueueStats = {
    total: operations.length,
    byStatus: {
      [QueuedOperationStatus.PENDING]: 0,
      [QueuedOperationStatus.PROCESSING]: 0,
      [QueuedOperationStatus.COMPLETED]: 0,
      [QueuedOperationStatus.FAILED]: 0,
      [QueuedOperationStatus.CANCELLED]: 0,
    },
    byType: {
      [QueuedOperationType.CREATE]: 0,
      [QueuedOperationType.UPDATE]: 0,
      [QueuedOperationType.DELETE]: 0,
      [QueuedOperationType.UPLOAD]: 0,
      [QueuedOperationType.RPC]: 0,
      [QueuedOperationType.CUSTOM]: 0,
    },
    byPriority: {
      [QueuePriority.LOW]: 0,
      [QueuePriority.NORMAL]: 0,
      [QueuePriority.HIGH]: 0,
      [QueuePriority.CRITICAL]: 0,
    },
  };

  operations.forEach(op => {
    stats.byStatus[op.status]++;
    stats.byType[op.type]++;
    stats.byPriority[op.priority]++;
  });

  if (operations.length > 0) {
    stats.oldestOperation = Math.min(...operations.map(op => op.queuedAt));
  }

  return stats;
}

/**
 * Format operation for display
 */
export function formatOperationDescription(operation: QueuedOperation): string {
  if (operation.description) {
    return operation.description;
  }

  const typeLabel = operation.type.charAt(0).toUpperCase() + operation.type.slice(1);
  return `${typeLabel} ${operation.resource}`;
}

/**
 * Clean expired operations from queue
 */
export function cleanExpiredOperations(
  operations: QueuedOperation[]
): QueuedOperation[] {
  return operations.filter(op => !isOperationExpired(op));
}

/**
 * Remove completed operations from queue
 */
export function removeCompletedOperations(
  operations: QueuedOperation[],
  olderThan?: number
): QueuedOperation[] {
  const cutoff = olderThan || Date.now() - 60 * 60 * 1000; // 1 hour default

  return operations.filter(
    op =>
      op.status !== QueuedOperationStatus.COMPLETED ||
      op.queuedAt > cutoff
  );
}
